import { useMutation, useQuery } from '@tanstack/react-query';
import {
  acceptMandateRequest,
  autoScrapeOff,
  autoScrapeOn,
  deleteTaxAgent,
  getOnboardingStatus,
  getSettings,
  getTaxAgent,
  rejectMandateRequest,
} from '~apis/settings';
import { useCompanyId } from '~hooks/useCompanyId';
import { queryClient } from '~libs/queryClient';

export const SettingsQueryKey = {
  default: () => ['settings'],
  onboardingStatus: ({ companyId }: { companyId: string }) => [
    ...SettingsQueryKey.default(),
    companyId,
    'onboardingStatus',
  ],
  taxAgent: ({ companyId }: { companyId: string }) => [
    ...SettingsQueryKey.default(),
    companyId,
    'taxAgent',
  ],
};

export function useGetSettings(refetch?: boolean) {
  const { companyId } = useCompanyId();
  return useQuery({
    queryKey: [...SettingsQueryKey.default(), companyId],
    queryFn: () => getSettings(companyId),
    enabled: !!companyId,
    refetchOnMount: refetch,
  });
}

export function useAutoScrapeOn() {
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: () => autoScrapeOn({ companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries(SettingsQueryKey.default());
    },
  });
}

export function useAutoScrapeOff() {
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: () => autoScrapeOff({ companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries(SettingsQueryKey.default());
    },
  });
}

export function useGetOnboardingStatus() {
  const { companyId } = useCompanyId();
  return useQuery({
    queryKey: [...SettingsQueryKey.onboardingStatus({ companyId })],
    queryFn: () => getOnboardingStatus({ companyId }),
    enabled: !!companyId,
  });
}

export function useAcceptMandateRequest() {
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: ({ taxClientId }: { taxClientId: string }) =>
      acceptMandateRequest({ companyId, taxClientId }),
    onSuccess: () => {
      queryClient.invalidateQueries([...SettingsQueryKey.default(), companyId]);
    },
  });
}

export function useRejectMandateRequest() {
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: ({ taxClientId }: { taxClientId: string }) =>
      rejectMandateRequest({ companyId, taxClientId }),
    onSuccess: () => {
      queryClient.invalidateQueries([...SettingsQueryKey.default(), companyId]);
    },
  });
}

export function useGetTaxAgent() {
  const { companyId } = useCompanyId();
  return useQuery({
    queryKey: [...SettingsQueryKey.taxAgent({ companyId })],
    queryFn: () => getTaxAgent({ companyId }),
    enabled: !!companyId,
  });
}

export function useDeleteTaxAgent() {
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: ({ taxClientId }: { taxClientId: string }) =>
      deleteTaxAgent({ companyId, taxClientId }),
    onSuccess: () => {
      queryClient.invalidateQueries([...SettingsQueryKey.default(), companyId]);
      queryClient.invalidateQueries([...SettingsQueryKey.taxAgent({ companyId })]);
    },
  });
}
